import { get, post, del } from './api'

export interface Sailing {
  id: string; route: string; departs_on: string
  scheduled_departure: number; scheduled_arrival: number
  status: string; actual_departure: number | null; status_note: string | null
  /* The timetable's own footnote, and the time it printed. Separate from
     status_note, which is an operational message about today — see migration
     0106. `movedByFootnote` is the server's judgement, not a comparison the UI
     is asked to make. */
  printed_departure: number | null; schedule_note: string | null
  restricted: string | null; movedByFootnote: boolean
  vessel_name: string; vessel_kind: string
  passenger_capacity: number; vehicle_capacity: number | null
  booked: number; seatsLeft: number; delayMinutes: number | null
  /* Decided by the SERVER, so what the list shows and what the API accepts
     cannot drift apart. */
  bookable: boolean; bookableWithVehicle: boolean
  departed: boolean; closesAt: number; vehicleClosesAt: number; minutesToClose: number
}

export interface LastChance {
  route: string; sailingId: string; departsAt: number
  closesAt: number; minutesToClose: number; vesselName: string
}
export interface Position { lat: number; lng: number; progress: number | null; heading: number | null; speed_knots: number | null; at: number }
export interface Traveller {
  id: string; name: string; kind: string; needs_priority: number
  note: string | null; last_used_at: number | null
}

export interface Me {
  residency: { status: string; municipality?: string; license_last4?: string; reject_reason?: string }
  vehicles: { id: string; plate: string; make: string | null; status: string }[]
  isResident: boolean; canChooseSeat: boolean; canBringVehicle: boolean
}

export const ROUTES = [
  { key: 'ceiba-vieques', label: 'Ceiba → Vieques' },
  { key: 'vieques-ceiba', label: 'Vieques → Ceiba' },
  { key: 'ceiba-culebra', label: 'Ceiba → Culebra' },
  { key: 'culebra-ceiba', label: 'Culebra → Ceiba' },
]

export const ferry = {
  sailings: (date?: string, route?: string) => {
    const q = new URLSearchParams()
    if (date) q.set('date', date)
    if (route) q.set('route', route)
    return get<{ date: string; now: number; sailings: Sailing[]; lastChance: LastChance[]; closedRoutes: string[] }>(`/api/sailings?${q}`)
  },
  position: (id: string) => get<{ sailing: Record<string, unknown>; position: Position | null }>(`/api/sailings/${id}/position`),
  me: () => get<Me>('/api/ferry/me'),
  claimResidency: (b: Record<string, unknown>) => post<{ status: string }>('/api/ferry/residency', b),
  addVehicle: (b: Record<string, unknown>) => post<{ id: string }>('/api/ferry/vehicles', b),
  book: (b: Record<string, unknown>) => post<{ id: string; reference: string; qr_token: string; total_cents: number; isResidentRate: boolean }>('/api/bookings', b),
  booking: (id: string) => get<{ booking: Record<string, unknown>; passengers: Record<string, unknown>[]; extras: Record<string, unknown>[]; pass: { token: string; partySize: number; resident: boolean } }>(`/api/bookings/${id}`),
  scan: (token: string, sailingId?: string) => post<{ outcome: string; partySize?: number; resident?: boolean; message?: string; reference?: string }>('/api/bookings/scan', { token, sailingId }),
  travellers: () => get<{ travellers: Traveller[] }>('/api/ferry/travellers'),
  forgetTraveller: (id: string) => del<{ ok: true }>(`/api/ferry/travellers/${id}`),
  passport: () => get<Passport>('/api/ferry/passport'),
  trips: (travelMinutes?: number | null) =>
    get<TripsView>(`/api/ferry/trips${travelMinutes ? `?travelMinutes=${travelMinutes}` : ''}`),
  pay: (id: string, body: Record<string, unknown> = {}) =>
    post<{ ok: boolean; demo: boolean; demoNote?: string; alreadyPaid?: boolean; reference: string; paidCents?: number }>(`/api/bookings/${id}/pay`, body),
  cancelBooking: (id: string) => post<{ ok: true }>(`/api/bookings/${id}/cancel`, {}),
  ops: {
    today: () => get<{ date: string; sailings: TodaySailing[] }>('/api/ferry/ops/today'),
    dashboard: (days = 30) => get<OpsDashboard>(`/api/ferry/ops/dashboard?days=${days}`),
  },
}

export interface OpsDashboard {
  window: { from: string; to: string; days: number }
  punctuality: { route: string; sailings: number; onTimePct: number | null; cancelled: number; avgDelayMinutes: number }[]
  load: {
    sailings: number; seatsOffered: number; seatsSold: number; loadFactorPct: number; emptySeats: number
    byHour: { hour: number; seats: number; booked: number; loadPct: number }[]
  }
  revenue: { gross: number; residentShare: number; bookings: number; passengers: number; perPassenger: number }
  leakage: { paidNeverBoarded: number; abandonedHolds: number }
  gate: {
    peopleBoarded: number; scans: number; peoplePerScan: number; refused: number
    refusedBreakdown: { outcome: string; n: number }[]
    minutesSaved: number
    assumptions: { secondsPerPersonManual: number; secondsPerScan: number; note: string }
  }
  whereToLook: { headline: string; detail: string }[]
}

export const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
export const clock = (ms: number) =>
  new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Puerto_Rico' }).format(ms)


export interface Trip {
  id: string; reference: string; status: string; party_size: number
  total_cents: number; route: string; vessel_name: string
  scheduled_departure: number; scheduled_arrival: number
  sailing_status: string; actual_departure: number | null
  hasVehicle: boolean; isResident: boolean
  urgency: 'now' | 'soon' | 'upcoming' | 'past'
  leaveHomeAt: number | null; cutoffAt: number
  minutesToLeave: number | null; minutesToDeparture: number; delayMinutes: number | null
}

export type StampId =
  | 'first' | 'vieques' | 'culebra' | 'bothIslands'
  | 'sunrise' | 'sunset' | 'family' | 'roundTrip'
  | 'regular' | 'islander'

export interface Passport {
  crossings: number; travellers: number; earned: number
  stamps: { id: StampId; earned: boolean; earnedAt: number | null; progress?: { have: number; need: number } }[]
}

export interface TripsView {
  now: number
  trips: Trip[]; upcoming: Trip[]; past: Trip[]
  remaining: number; unpaid: number
  next: Trip | null; needsAttention: boolean
}

/** "in 2h 14m" / "in 45m" / "5m ago" — the shape a countdown is read in. */
export function countdown(minutes: number): string {
  const past = minutes < 0
  const m = Math.abs(minutes)
  const text = m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`
  return past ? `${text} ago` : `in ${text}`
}

/** Where a route goes, said as a destination rather than a slug. */
export const PLACE: Record<string, string> = {
  ceiba: 'Ceiba', vieques: 'Vieques', culebra: 'Culebra',
}
export const destinationOf = (route: string) => PLACE[route.split('-')[1] ?? ''] ?? route
export const originOf = (route: string) => PLACE[route.split('-')[0] ?? ''] ?? route


/**
 * A sailing on the live board.
 *
 * Typed rather than `Record<string, unknown>`, which is how the board came to
 * render `NaN` in the boarded column: the API returns `aboard`, the table read
 * `boarded`, and an index signature made both look equally valid to the
 * compiler. An interface turns that into a build error instead of a number
 * nobody on the dock can interpret.
 */
export interface TodaySailing {
  id: string
  route: string
  status: string
  scheduled_departure: number
  actual_departure: number | null
  status_note: string | null
  vessel_name: string
  vessel_kind: string
  passenger_capacity: number
  booked: number
  aboard: number
  stillToBoard: number
  loadPct: number
}
